import React from "react";

import { Picto, Spinner } from "@amphore-dev/amphore-lib";
import { t } from "i18next";
import { toast } from "react-toastify";

import { useMutation } from "@tanstack/react-query";

import { Avatar, IAvatarProps } from "@/components/atoms";

import { cn } from "@/utils/cn";

export interface IEditableAvatarProps extends IAvatarProps {
	wrapperClassName?: string;
	disabled?: boolean;
	maxSize?: number; // in bytes
	/** What the empty avatar says, and the file input's accessible name. */
	uploadLabel?: string;
	onChange?: (file: File) => void | Promise<unknown>;
	onError?: (error: "size") => void;
}

export const EditableAvatar: React.FC<IEditableAvatarProps> = ({
	disabled,
	maxSize = 2 * 1024 * 1024, // 2MB default
	onChange,
	onError,
	className,
	wrapperClassName,
	rounded = true,
	uploadLabel,
	...props
}) => {
	const emptyLabel = uploadLabel ?? t("file.upload");

	const handleChange = (file: File) => {
		const prom = onChange?.(file);
		if (prom instanceof Promise) {
			return prom;
		}

		return Promise.resolve(prom);
	};

	const changeMutation = useMutation({
		mutationFn: handleChange,
	});

	return (
		<div className={wrapperClassName ?? className}>
			<Avatar
				{...props}
				rounded={rounded}
				className={cn([
					"relative",
					!props.url &&
						"rounded-xl bg-neutral-100 duration-300 hover:bg-neutral-200",
					className,
				])}
			>
				{!disabled && (
					<div
						className={cn([
							"group/avatar relative flex h-full w-full items-center justify-center overflow-hidden",
							rounded && "rounded-full",
						])}
					>
						<div
							className={cn([
								"pointer-events-none z-10 flex flex-col items-center justify-center gap-2 text-center text-base font-bold text-white opacity-0 transition-opacity duration-300 group-hover/avatar:opacity-100",
								(!props.url || changeMutation.isPending) &&
									"opacity-100",
								!props.url && "text-neutral-400",
							])}
						>
							{changeMutation.isPending ? (
								<Spinner size="lg" />
							) : (
								<>
									<Picto
										icon={props.url ? "pen" : "upload"}
										className="h-6 w-6"
									/>
									<span
										className={cn([
											!props.url && "text-sm leading-5",
										])}
									>
										{props.url
											? t("global.edit")
											: emptyLabel}
									</span>
								</>
							)}
						</div>
						<input
							type="file"
							accept=".jpg,.jpeg,.png"
							aria-label={emptyLabel}
							className={cn([
								"absolute inset-0 z-20 cursor-pointer opacity-0",
								rounded && "rounded-full",
								changeMutation.isPending && "cursor-wait",
								disabled && "cursor-not-allowed",
							])}
							disabled={disabled || changeMutation.isPending}
							onChange={(e) => {
								const file = e.target.files?.[0];
								if (!file) return;
								if (maxSize && file.size > maxSize) {
									e.target.value = "";
									toast.error(
										t("file.sizeError", {
											size: (
												maxSize /
												(1024 * 1024)
											).toFixed(2),
										})
									);
									onError?.("size");
									return;
								}
								const input = e.target;
								changeMutation
									.mutateAsync(file)
									// The caller's own toast says why it failed.
									.catch(() => undefined)
									// Emptied either way: picking the same file
									// again after a failure must fire a change.
									.finally(() => {
										input.value = "";
									});
							}}
						/>
						<div
							className={cn([
								"absolute inset-0 bg-black opacity-0 transition-opacity duration-300 group-hover/avatar:opacity-70",
								changeMutation.isPending && "opacity-50",
								!props.url &&
									"opacity-0 group-hover/avatar:opacity-0",
							])}
						/>
					</div>
				)}
			</Avatar>
		</div>
	);
};
